import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Webcam from "react-webcam";
import { getPoseOptIn, setPoseOptIn, isCapture as getIsCaptureFlag } from "../poseRecorder.ts";
const getIsCapture = () => getIsCaptureFlag;

// Legacy-shaped result so the drawing code below stays untouched
type PoseResults = {
  image: HTMLVideoElement;
  poseLandmarks: Array<{ x: number; y: number; z: number; visibility?: number }>;
  poseWorldLandmarks: Array<{ x: number; y: number; z: number; visibility?: number }>;
  // Player 2 (2P modes) — drawn in a second color, sent on its own channel
  p2Landmarks?: Array<{ x: number; y: number; z: number; visibility?: number }>;
};

// BlazePose topology (same 33 landmarks as the legacy API)
const POSE_CONNECTIONS: [number, number][] = [
  [0, 1], [1, 2], [2, 3], [3, 7], [0, 4], [4, 5], [5, 6], [6, 8], [9, 10],
  [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],
  [15, 17], [15, 19], [15, 21], [17, 19], [16, 18], [16, 20], [16, 22], [18, 20],
  [11, 23], [12, 24], [23, 24], [23, 25], [24, 26],
  [25, 27], [26, 28], [27, 29], [28, 30], [29, 31], [30, 32], [27, 31], [28, 32],
];

const SMOOTHING_WINDOW = 5;
// Arms get a much shorter window: 5 frames (~250ms at packet rate) ate half
// the amplitude of a fast swing — the avatar barely moved while the player
// flailed. Squat detection reads shoulders/hips, which keep the long window.
const ARM_SMOOTHING_WINDOW = 2;
const isArmLandmark = (i: number) => i >= 13 && i <= 22; // elbows, wrists, hands

// (x, y, z, visibility)
type LandmarkTuple = [number, number, number, number];

const WebcamPoseProvider: React.FC = () => {
  const webcamRef = useRef<Webcam>(null);
  // iOS kills the camera stream while the app is suspended and leaves the
  // last frame on screen (Joshua 2026-09-08: "when I change apps and come
  // back the camera doesn't work"). Remounting the Webcam re-requests it;
  // the pose loop reads webcamRef.current.video each frame so it follows.
  const [camKey, setCamKey] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const lastReacquireRef = useRef(0);
  // Re-request the camera: stop the old tracks FIRST (iOS allows one capture
  // per page - a request while the old one is alive comes back black), give
  // Safari a beat to release it, then remount the Webcam.
  const reacquire = () => {
    const now = performance.now();
    if (now - lastReacquireRef.current < 4000) return;
    lastReacquireRef.current = now;
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* already gone */ }
    streamRef.current = null;
    window.setTimeout(() => setCamKey((k) => k + 1), 350);
  };
  useEffect(() => {
    // iOS Safari (Joshua 2026-09-13): swiping down Control Center never
    // fires visibilitychange, yet the camera is suspended - on return the
    // video shows ONE frame and stays paused/muted. So: a 1 s watchdog on
    // whether video time advances, play() first, re-acquire only after a
    // 2 s stall. A muted track is NOT dead - iOS mutes for ~1 s on resume.
    let stalled = 0;
    let lastT = -1;
    const nudge = () => {
      if (document.visibilityState !== "visible") return;
      const v = webcamRef.current?.video as HTMLVideoElement | undefined;
      const st = v?.srcObject as MediaStream | null | undefined;
      const t = st?.getVideoTracks?.()[0];
      if (!v || !t || t.readyState === "ended") { reacquire(); return; }
      if (v.paused) v.play().catch(() => {});
    };
    const id = window.setInterval(() => {
      if (document.visibilityState !== "visible") { stalled = 0; return; }
      const v = webcamRef.current?.video as HTMLVideoElement | undefined;
      if (!v || v.readyState < 2) return;
      if (v.currentTime === lastT) {
        stalled++;
        if (stalled === 1) v.play().catch(() => {});
        if (stalled >= 2) { stalled = 0; reacquire(); }
      } else stalled = 0;
      lastT = v.currentTime;
    }, 1000);
    const onVis = () => window.setTimeout(nudge, 400);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pageshow", onVis);
    window.addEventListener("focus", onVis);
    return () => { window.clearInterval(id); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("pageshow", onVis); window.removeEventListener("focus", onVis); };
  }, []);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [results, setResults] = useState<PoseResults | null>(null);

  // Per-landmark smoothing buffers (0..35) PER PLAYER – mirrors Python's
  // landmark_buffers; each tracked person needs independent history
  const landmarkBuffersRef = useRef<{ [player: number]: { [key: number]: LandmarkTuple[] } }>({});

  // Lazy init buffers once
  if (Object.keys(landmarkBuffersRef.current).length === 0) {
    for (let p = 0; p < 2; p++) {
      landmarkBuffersRef.current[p] = {};
      for (let i = 0; i < 36; i++) landmarkBuffersRef.current[p][i] = [];
    }
  }

  // Push into buffer and return smoothed value (like Python)
  const pushAndSmooth = (player: number, index: number, current: LandmarkTuple): LandmarkTuple => {
    const buffer = landmarkBuffersRef.current[player][index];
    const window = isArmLandmark(index) ? ARM_SMOOTHING_WINDOW : SMOOTHING_WINDOW;
    buffer.push(current);
    if (buffer.length > window) buffer.shift();

    if (buffer.length < Math.min(3, window)) return current;

    let sx = 0;
    let sy = 0;
    let sz = 0;
    let sv = 0;
    for (const [x, y, z, v] of buffer) {
      sx += x;
      sy += y;
      sz += z;
      sv += v;
    }
    const n = buffer.length;
    return [sx / n, sy / n, sz / n, sv / n];
  };

  // Device acceleration magnitude (gravity removed) and gyroscope rotation rate
  // (deg/s), decayed between events. Sent as pseudo-landmark ID 37 (x = accel,
  // y = rotation) so Unity can ignore bob while the phone shakes OR tilts.
  const deviceMotionRef = useRef(0);
  const deviceRotationRef = useRef(0);
  // Phone lean-back angle (deviceorientation beta, degrees; 0 = upright).
  // Rides in landmark 37's z slot for the in-game debug tab and, later,
  // tilt-compensated depth normalization.
  const deviceTiltRef = useRef(0);
  const onMotionRef = useRef<((e: DeviceMotionEvent) => void) | null>(null);
  const onOrientRef = useRef<((e: DeviceOrientationEvent) => void) | null>(null);

  // EVERYONE gets the start gate: on phones the tap doubles as the user
  // gesture iOS requires for motion-sensor permission; on desktop it unlocks
  // the audio context and gives the game a proper title moment (it used to
  // boot straight in — a dev-era shortcut). isMobile still steers the copy.
  const isMobile =
    typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const [started, setStarted] = useState(false);
  // Setup screen after the first tap: prop the phone, step back, squat —
  // with live readiness checks before play begins
  const [showSetup, setShowSetup] = useState(false);
  // Tutorial card index — one idea per card, tap to advance. A single wall of
  // bullets gets dismissed unread; four pictures get absorbed.
  const [setupStep, setSetupStep] = useState(0);
  const [phoneSteady, setPhoneSteady] = useState(true);
  // Camera feed overlay top-right — open by default so players see themselves
  // tracked; tap collapses it to the button
  const [feedVisible, setFeedVisible] = useState(true);
  // Debug view: plot key landmarks relative to the hip centre — phone tilt and
  // framing cancel out, leaving pure body motion
  const [showPoints, setShowPoints] = useState(false);
  // Consented pose-data sharing ("help improve" card) — see poseRecorder.ts
  const [poseShare, setPoseShareState] = useState(getPoseOptIn());
  const [showShareCard, setShowShareCard] = useState(false);
  // Explicit fullscreen control (Joshua 2026-08-29): auto-on-start can be
  // missed or exited. On iPhone the API is blocked, so the button re-shows
  // the add-to-home-screen hint instead.
  const [inFullscreen, setInFullscreen] = useState(false);
  // Hide the FULLSCREEN pill when it cannot help: launched from the home
  // screen (already fullscreen) or mid-run, where it covered the HUD's
  // top-left (Joshua 2026-09-06)
  const standalone = typeof window !== "undefined" && (
    (window.matchMedia && (window.matchMedia("(display-mode: standalone)").matches || window.matchMedia("(display-mode: fullscreen)").matches))
    || (navigator as unknown as { standalone?: boolean }).standalone === true);
  // iPhone Safari cannot fullscreen a page, and its bars now match the game
  // (Joshua 2026-09-07: "keep it for Android only")
  const isIOS = typeof navigator !== "undefined" && (/iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
  const [running, setRunning] = useState(false);
  const wakeRef = useRef<{ release: () => Promise<void> } | null>(null);
  useEffect(() => {
    const on = (e: Event) => {
      try {
        const j = JSON.parse((e as CustomEvent).detail.j);
        if (j.event === "run_start") {
          setRunning(true);
          // keep the screen awake for the run (a phone dims mid-squat)
          const wl = (navigator as unknown as { wakeLock?: { request: (t: string) => Promise<{ release: () => Promise<void> }> } }).wakeLock;
          if (wl && !wakeRef.current) wl.request("screen").then((l) => { wakeRef.current = l; }).catch(() => {});
        } else if (j.event === "run_end" || j.event === "run_abort" || j.event === "quest_end" || j.event === "menu") {
          setRunning(false);
          if (wakeRef.current) { wakeRef.current.release().catch(() => {}); wakeRef.current = null; }
        }
      } catch { /* not ours */ }
    };
    window.addEventListener("telemetry", on);
    return () => window.removeEventListener("telemetry", on);
  }, []);
  useEffect(() => {
    const onFs = () => setInFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const enterFullscreen = () => {
    const isIPhone = /iPhone/.test(navigator.userAgent);
    if (isIPhone) {
      try { localStorage.removeItem("aots_a2hs_hint"); } catch { /* ok */ }
      window.dispatchEvent(new CustomEvent("showA2HSHint"));
      return;
    }
    try {
      const el = document.documentElement as any;
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
    } catch { /* unsupported */ }
  };
  // Border flash when Unity counts a rep (via the ReactBridge jslib event):
  // "" = idle, "rep" = green pulse, "burst" = orange pulse. Deliberately driven
  // by the DETECTOR, not our own pose math — it flashes only when the game
  // actually heard the squat, so it doubles as field diagnostics.
  const [repFlash, setRepFlash] = useState("");
  // Engine load failure — shown on the feed instead of a silent black box
  const [engineError, setEngineError] = useState("");
  // 2P feasibility benchmark (?poses=2): live inference rate + pose count on
  // the feed, so multiplayer viability is measured before it's built
  const [benchInfo, setBenchInfo] = useState("");
  // Depth meter: springs to the rep's depth, then drains — reactive ("it felt
  // that!") rather than a continuous measurement, which would break the magic
  const [depthLevel, setDepthLevel] = useState(0);
  // High-water mark: a tick at the PREVIOUS rep's depth, lingering ~1.2s, so
  // "was that deeper than my last one?" is answered at a glance
  const [peakMark, setPeakMark] = useState(0);
  const prevDepthRef = useRef(0);
  useEffect(() => {
    let timer = 0;
    let drain = 0;
    let peak = 0;
    const onRep = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const depth = Math.max(0.12, Math.min(1, d.depth || 0));
      setRepFlash(d.burst ? "burst" : "rep");
      setDepthLevel(depth);
      setPeakMark(prevDepthRef.current);
      prevDepthRef.current = depth;
      window.clearTimeout(timer);
      window.clearTimeout(drain);
      window.clearTimeout(peak);
      timer = window.setTimeout(() => setRepFlash(""), d.burst ? 500 : 280);
      drain = window.setTimeout(() => setDepthLevel(0), d.burst ? 450 : 250);
      peak = window.setTimeout(() => setPeakMark(0), 2000);
    };
    window.addEventListener("repEvent", onRep);
    return () => {
      window.removeEventListener("repEvent", onRep);
      window.clearTimeout(timer);
      window.clearTimeout(drain);
      window.clearTimeout(peak);
    };
  }, []);
  // Shared-camera 2P winner (Unity fires winnerEvent at the finish): the
  // winner's skeleton turns gold with a WINNER! crown pill for a few
  // seconds, and the panel border glows gold (Joshua 2026-09-09)
  const [winner, setWinner] = useState(0);
  const winnerRef = useRef(0);
  useEffect(() => {
    let t = 0;
    const onWin = (e: Event) => {
      const who = (e as CustomEvent).detail?.who | 0;
      winnerRef.current = who; setWinner(who);
      window.clearTimeout(t);
      t = window.setTimeout(() => { winnerRef.current = 0; setWinner(0); }, 6000);
    };
    window.addEventListener("winnerEvent", onWin);
    return () => { window.removeEventListener("winnerEvent", onWin); window.clearTimeout(t); };
  }, []);
  const flashColor =
    winner ? "#fbbf24" : repFlash === "burst" ? "#fb923c" : repFlash === "rep" ? "#4ade80" : null;

  // Mode how-to cards: Unity fires modeEvent when the player picks ZOMBIES (1)
  // or BATTLE (2); we show 2 visual cards and answer with ModeCardDone so the
  // countdown can start. Kept SHORT — walls of text get dismissed unread.
  const [modeCards, setModeCards] = useState<{ mode: number; step: number } | null>(null);
  useEffect(() => {
    const onMode = (e: Event) => {
      const m = (e as CustomEvent).detail?.mode || 0;
      if (m === 1 || m === 2) setModeCards({ mode: m, step: 0 });
    };
    window.addEventListener("modeEvent", onMode);
    return () => window.removeEventListener("modeEvent", onMode);
  }, []);
  const advanceModeCard = () => {
    if (!modeCards) return;
    if (modeCards.step < 1) {
      setModeCards({ ...modeCards, step: modeCards.step + 1 });
    } else {
      setModeCards(null);
      try {
        (window as any).unityInstance?.SendMessage("Manager", "ModeCardDone");
      } catch {}
    }
  };

  // Unity installs its own audio-resume on window mousedown/touchstart, but only
  // once its audio subsystem is up — which is after the whole build downloads.
  // Tapping before then means the gesture is missed, and on a phone the player
  // never touches the screen again, so the game runs silent forever. Hold the
  // start gate closed until Unity exists so the tap always lands after it.
  const [unityReady, setUnityReady] = useState(false);
  // The pose engine's model load + GPU setup freezes the page for a few
  // seconds right after Unity appears - taps on the tutorial cards were
  // swallowed (Joshua 2026-10-20: "not responsive for a bit"). Keep the gate
  // on "Loading…" until it lands so the freeze happens BEFORE the first tap.
  const [poseReady, setPoseReady] = useState(false);
  const gateReady = unityReady && poseReady;

  // Safety valve: a hung/failed engine must never strand the start gate
  useEffect(() => {
    if (!unityReady || poseReady) return;
    const id = window.setTimeout(() => setPoseReady(true), 20000);
    return () => window.clearTimeout(id);
  }, [unityReady, poseReady]);

  useEffect(() => {
    // capture mode is standalone - no Unity instance will ever appear
    if (getIsCapture()) { setUnityReady(true); return; }
    if ((window as any).unityInstance) { setUnityReady(true); return; }
    const id = window.setInterval(() => {
      if ((window as any).unityInstance) {
        setUnityReady(true);
        window.clearInterval(id);
      }
    }, 250);
    return () => window.clearInterval(id);
  }, []);

  // Safari defaults the audio session to "auto", which behaves as "ambient":
  // silenced by the ringer switch and deprioritized while a capture stream is
  // live — and our camera runs the whole session. Declaring "playback" makes
  // the game audible regardless of the mute switch. Safari-only for now.
  const claimPlaybackAudioSession = () => {
    try {
      const session = (navigator as any).audioSession;
      if (session) session.type = "playback";
    } catch {
      // Not supported here — harmless, other browsers don't need it
    }
  };

  useEffect(claimPlaybackAudioSession, []);

  const startGame = () => {
    if (!gateReady) return;
    claimPlaybackAudioSession();
    // Fullscreen by default (Joshua 2026-08-29): this tap is the user
    // gesture the API needs. iOS Safari has no such API - the PWA
    // add-to-home-screen path covers it there.
    try {
      const el = document.documentElement as any;
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
    } catch { /* not supported */ }

    const DME = DeviceMotionEvent as any;
    if (typeof DME?.requestPermission === "function") {
      // iOS: must be called from inside this tap handler
      DME.requestPermission()
        .then((state: string) => {
          if (state === "granted" && onMotionRef.current) {
            window.addEventListener("devicemotion", onMotionRef.current);
          }
        })
        .catch(() => {});
    }
    const DOE = DeviceOrientationEvent as any;
    if (typeof DOE?.requestPermission === "function") {
      DOE.requestPermission()
        .then((state: string) => {
          if (state === "granted" && onOrientRef.current) {
            window.addEventListener("deviceorientation", onOrientRef.current);
          }
        })
        .catch(() => {});
    }
    setStarted(true);
    setShowSetup(!getIsCapture());   // capture mode: straight to the cue banner
    window.dispatchEvent(new Event("gameStarted"));   // room-link auto-join waits for this
  };

  // While the setup screen is up, poll the motion refs so the "phone is
  // steady" check is live. No devicemotion events (desktop) leaves the refs
  // at 0, which correctly reads as steady.
  useEffect(() => {
    if (!showSetup) return;
    const id = window.setInterval(() => {
      setPhoneSteady(deviceMotionRef.current < 0.8 && deviceRotationRef.current < 15);
    }, 250);
    return () => window.clearInterval(id);
  }, [showSetup]);

  useEffect(() => {
    const onMotion = (e: DeviceMotionEvent) => {
      const a = e.acceleration;
      if (a) {
        const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2);
        // Fast attack, slow decay so brief spikes still register in Unity
        deviceMotionRef.current = Math.max(mag, deviceMotionRef.current * 0.9);
      }
      const r = e.rotationRate;
      if (r) {
        const rot = Math.sqrt((r.alpha || 0) ** 2 + (r.beta || 0) ** 2 + (r.gamma || 0) ** 2);
        deviceRotationRef.current = Math.max(rot, deviceRotationRef.current * 0.9);
      }
    };

    onMotionRef.current = onMotion;

    // Beta = lean-back: 0° upright on its edge, 90° flat on its back
    const onOrient = (e: DeviceOrientationEvent) => {
      if (e.beta != null) deviceTiltRef.current = Math.abs(90 - Math.abs(e.beta));
    };
    onOrientRef.current = onOrient;

    const DME = DeviceMotionEvent as any;
    if (typeof DME?.requestPermission !== "function") {
      // Non-iOS: no permission gate, listen right away
      window.addEventListener("devicemotion", onMotion);
      window.addEventListener("deviceorientation", onOrient);
    }

    return () => {
      window.removeEventListener("devicemotion", onMotion);
      window.removeEventListener("deviceorientation", onOrient);
    };
  }, []);

  useEffect(() => {
    // MediaPipe Tasks PoseLandmarker (the legacy Pose API was discontinued in
    // 2023). Same 33-landmark topology; fixture-compared against the legacy
    // engine before switching — stroke depths match and hip-visibility margins
    // improved 2-3x on the live framing. WASM + model served locally so itch
    // or CSP environments never need a CDN.
    let landmarker: any = null;
    let raf = 0;
    let disposed = false;
    let removeNumPosesListener: (() => void) | null = null;
    let lastVideoTime = -1;

    // Build one player's packet (world landmarks + derived centers), smoothed
    // through that player's own buffers, and dispatch on their channel.
    const emitPlayer = (player: number, im: PoseResults["poseLandmarks"], world: PoseResults["poseWorldLandmarks"]) => {
      const data: number[] = [];
      try {
        if (!world || world.length < 33) throw new Error("world landmarks missing");

        // 0..32 from WORLD landmarks (smoothed)
        for (let i = 0; i < 33; i++) {
          const lm = world[i];
          const current: LandmarkTuple = [lm.x ?? 0, lm.y ?? 0, lm.z ?? 0, lm.visibility ?? 0];
          const smoothed = pushAndSmooth(player, i, current);
          data.push(i, smoothed[0], smoothed[1], smoothed[2], smoothed[3]);
        }

        if (!im || im.length < 25) throw new Error("image landmarks missing");

        const hipCurrent: LandmarkTuple = [
          (im[23].x + im[24].x) / 2, (im[23].y + im[24].y) / 2,
          ((im[23].z || 0) + (im[24].z || 0)) / 2, 1.0];
        const hipSmoothed = pushAndSmooth(player, 33, hipCurrent);
        data.push(34, hipSmoothed[0], hipSmoothed[1], hipSmoothed[2], hipSmoothed[3]);

        const headCurrent: LandmarkTuple = [
          (im[1].x + im[4].x) / 2, (im[1].y + im[4].y) / 2,
          ((im[1].z || 0) + (im[4].z || 0)) / 2, 1.0];
        const headSmoothed = pushAndSmooth(player, 34, headCurrent);
        data.push(35, headSmoothed[0], headSmoothed[1], headSmoothed[2], headSmoothed[3]);

        const shoulderCurrent: LandmarkTuple = [
          (im[11].x + im[12].x) / 2, (im[11].y + im[12].y) / 2,
          ((im[11].z || 0) + (im[12].z || 0)) / 2, 1.0];
        const shoulderSmoothed = pushAndSmooth(player, 35, shoulderCurrent);
        data.push(36, shoulderSmoothed[0], shoulderSmoothed[1], shoulderSmoothed[2], shoulderSmoothed[3]);

        // Device motion pseudo-landmark (the PHONE's — player 1 only)
        if (player === 0)
          data.push(37, deviceMotionRef.current, deviceRotationRef.current, deviceTiltRef.current, 1.0);
      } catch {
        // data stays empty, same contract as before
      }
      window.dispatchEvent(new CustomEvent(player === 0 ? "poseData" : "poseData2", { detail: data }));
    };

    // Identity tracking: pose indices from the detector are NOT stable
    // between frames, so players are matched to their previous hip centers
    // (image space, nearest-neighbor). First sighting: leftmost ON SCREEN is
    // P1 — the feed is mirrored, so screen-left = larger image x.
    let prevHipX: (number | null)[] = [null, null];
    const hipX = (im: any[]) => im && im.length > 24 ? (im[23].x + im[24].x) / 2 : 0.5;

    const assignPlayers = (r: any): [number | null, number | null] => {
      const n = r.landmarks?.length || 0;
      if (n === 0) return [null, null];
      if (n === 1) {
        const x = hipX(r.landmarks[0]);
        // One person: keep them on whichever slot they're closer to
        if (prevHipX[0] === null && prevHipX[1] === null) { prevHipX[0] = x; return [0, null]; }
        const d0 = prevHipX[0] !== null ? Math.abs(x - prevHipX[0]!) : 9;
        const d1 = prevHipX[1] !== null ? Math.abs(x - prevHipX[1]!) : 9;
        if (d1 < d0) { prevHipX[1] = x; return [null, 0]; }
        prevHipX[0] = x; return [0, null];
      }
      // Two people: pair to minimize total drift; unseeded = screen-left is P1
      const xa = hipX(r.landmarks[0]);
      const xb = hipX(r.landmarks[1]);
      let straight: boolean;
      if (prevHipX[0] === null || prevHipX[1] === null) {
        straight = xa > xb;   // mirrored feed: larger image x = screen left = P1
      } else {
        straight = Math.abs(xa - prevHipX[0]!) + Math.abs(xb - prevHipX[1]!)
                <= Math.abs(xb - prevHipX[0]!) + Math.abs(xa - prevHipX[1]!);
      }
      const p1Idx = straight ? 0 : 1;
      const p2Idx = straight ? 1 : 0;
      prevHipX[0] = hipX(r.landmarks[p1Idx]);
      prevHipX[1] = hipX(r.landmarks[p2Idx]);
      return [p1Idx, p2Idx];
    };

    // ONE pose by default — a landmarker initialized for 2 does pay per-frame
    // cost even with one person in frame (the always-2 benchmark only showed
    // that a second DETECTED person is cheap). Unity raises this to 2 when
    // the player picks 2P versus (numPosesEvent via ReactBridge.jslib) and
    // drops back to 1 on any other mode pick. ?poses=2 forces 2 for benching.
    const params = new URLSearchParams(window.location.search);
    let currentPoses = params.get("poses") === "2" ? 2 : 1;
    const modelName = params.get("model") === "lite" ? "pose_landmarker_lite.task" : "pose_landmarker_full.task";
    const benching = params.has("poses") || params.has("model");
    let benchFrames = 0;
    let benchPoses = 0;
    let benchStart = performance.now();

    const tick = () => {
      if (disposed) return;
      const video = webcamRef.current?.video;
      if (landmarker && video && video.readyState >= 2 && video.currentTime !== lastVideoTime) {
        lastVideoTime = video.currentTime;
        try {
          const r = landmarker.detectForVideo(video, performance.now());
          if (r.landmarks?.length && r.worldLandmarks?.length) {
            const [p1, p2] = assignPlayers(r);
            if (p1 !== null) emitPlayer(0, r.landmarks[p1] as any, r.worldLandmarks[p1] as any);
            if (p2 !== null) emitPlayer(1, r.landmarks[p2] as any, r.worldLandmarks[p2] as any);
            setResults({
              image: video,
              poseLandmarks: (p1 !== null ? r.landmarks[p1] : r.landmarks[0]) as any,
              poseWorldLandmarks: (p1 !== null ? r.worldLandmarks[p1] : r.worldLandmarks[0]) as any,
              p2Landmarks: p2 !== null ? (r.landmarks[p2] as any) : undefined,
            });
          }
          if (benching) {
            benchFrames++;
            benchPoses = r.landmarks?.length || 0;
            const dt = performance.now() - benchStart;
            if (dt >= 1000) {
              setBenchInfo(`${benchPoses} pose${benchPoses === 1 ? "" : "s"} · ${((benchFrames * 1000) / dt).toFixed(1)} Hz`);
              benchFrames = 0;
              benchStart = performance.now();
            }
          }
        } catch {
          // transient decode hiccup — skip the frame
        }
      }
      raf = requestAnimationFrame(tick);
    };

    (async () => {
      // Don't init the pose engine until Unity exists: on iOS the GPU delegate
      // opens a second WebGL context, and standing that up while Unity's own
      // context + heap are mid-load can hang the Unity loader. Pose data isn't
      // needed before the START tap (which already waits for unityReady).
      // EXCEPT capture mode: it never loads Unity, so this wait would spin
      // forever and no pose ever arrives (Joshua 2026-08-30, stuck card).
      while (!disposed && !(window as any).unityInstance && !getIsCapture()) {
        await new Promise((r) => setTimeout(r, 250));
      }
      if (disposed) return;
      // Resolve against the PAGE url, never the bundle's: a "./" specifier
      // inside the built main.js resolves to /static/js/... and 404s — which
      // silently killed the camera feed, keypoints, and all pose packets.
      const abs = (rel: string) => new URL(rel, document.baseURI).href;
      // Runtime import of our own served copy: CRA's webpack can't parse the
      // package's dynamic requires (and this keeps everything CDN-free)
      const mod: any = await import(/* webpackIgnore: true */ abs("wasm-vision/vision_bundle.mjs"));
      const { FilesetResolver, PoseLandmarker } = mod;
      const vision = await FilesetResolver.forVisionTasks(abs("wasm-vision"));
      const make = (delegate: "GPU" | "CPU", n: number) =>
        PoseLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: abs(`models/${modelName}`), delegate },
          runningMode: "VIDEO",
          numPoses: n,
          minPoseDetectionConfidence: 0.7,
          minTrackingConfidence: 0.7,
        });
      // GPU everywhere, CPU fallback. iOS was once forced to CPU on a
      // loading-hang theory, but the actual bug was an asset 404 — GPU
      // benchmarked ~2x faster on iPhone and played fine (2026-08-03).
      let activeDelegate: "GPU" | "CPU" = "GPU";
      try {
        landmarker = await make("GPU", currentPoses);
      } catch {
        landmarker = await make("CPU", currentPoses);
        activeDelegate = "CPU";
      }
      if (!disposed) setPoseReady(true);
      // Unity signals the needed pose count (1 solo / 2 versus). numPoses is
      // fixed at creation, so honoring it means rebuilding the landmarker;
      // tick() just skips frames while it's null mid-rebuild.
      let desiredPoses = currentPoses;
      let applying = false;
      const applyPoses = async () => {
        if (applying) return;
        applying = true;
        while (!disposed && desiredPoses !== currentPoses) {
          const n = desiredPoses;
          const old = landmarker;
          landmarker = null;
          try { old?.close(); } catch {}
          try {
            landmarker = await make(activeDelegate, n);
          } catch {
            landmarker = await make("CPU", n);
            activeDelegate = "CPU";
          }
          currentPoses = n;
        }
        applying = false;
      };
      const onNumPoses = (e: any) => {
        desiredPoses = e?.detail?.poses === 2 ? 2 : 1;
        void applyPoses().catch(() => {});
      };
      window.addEventListener("numPosesEvent", onNumPoses);
      removeNumPosesListener = () => window.removeEventListener("numPosesEvent", onNumPoses);
      if (!disposed) raf = requestAnimationFrame(tick);
    })().catch((e) => {
      console.error("pose engine failed:", e);
      if (!disposed) { setEngineError(String(e?.message || e)); setPoseReady(true); }
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      removeNumPosesListener?.();
      try { landmarker?.close(); } catch {}
    };
  }, []);

  useEffect(() => {
    if (!results || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = results.image as any;
    const w = img.videoWidth ?? img.width ?? 640;
    const h = img.videoHeight ?? img.height ?? 480;

    canvas.width = w;
    canvas.height = h;

    ctx.save();
    ctx.clearRect(0, 0, w, h);

    // Match Python display: cv2.flip(image, 1)
    // (Flip the canvas view; do NOT flip the data sent to Unity)
    ctx.translate(w, 0);
    ctx.scale(-1, 1);

    if (showPoints) {
      // Body-relative debug view: hip-centre pinned, so what you see is squat
      // motion only — no phone tilt, no framing offset
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      const lm = results.poseLandmarks;
      if (lm && lm.length >= 33) {
        const hipC = { x: (lm[23].x + lm[24].x) / 2, y: (lm[23].y + lm[24].y) / 2 };
        // Auto-fit over EVERY landmark — including the ones MediaPipe estimates
        // outside the camera frame — so the whole figure always fits the window
        let spanX = 0.01, spanY = 0.01;
        for (const l of lm) {
          spanX = Math.max(spanX, Math.abs(l.x - hipC.x));
          spanY = Math.max(spanY, Math.abs(l.y - hipC.y));
        }
        // Phones show a 3:4 crop of the canvas; desktop shows all of it
        const visibleW = isMobile ? h * 0.75 : w;
        const scale = Math.min((0.44 * h) / spanY, (0.44 * visibleW) / spanX);
        const proj = (l: { x: number; y: number }) => ({
          x: w / 2 + (l.x - hipC.x) * scale,
          y: h * 0.5 + (l.y - hipC.y) * scale,
        });
        ctx.strokeStyle = "rgba(255,255,255,0.55)";
        ctx.lineWidth = 3;
        ctx.lineCap = "round";
        for (const [a, b] of POSE_CONNECTIONS as [number, number][]) {
          const la = lm[a], lb = lm[b];
          if (!la || !lb) continue;
          const pa = proj(la), pb = proj(lb);
          ctx.beginPath();
          ctx.moveTo(pa.x, pa.y);
          ctx.lineTo(pb.x, pb.y);
          ctx.stroke();
        }
        for (let i = 0; i < lm.length; i++) {
          const pt = proj(lm[i]);
          // Colour by region: face dim white, arms cyan, hips gold, legs pink
          const c = i < 11 ? "rgba(255,255,255,0.5)"
                  : i <= 22 ? "#22d3ee"
                  : i <= 24 ? "#facc15"
                  : "#f472b6";
          ctx.fillStyle = c;
          // Dim the dots MediaPipe is guessing (off-frame / occluded)
          ctx.globalAlpha = Math.max(0.35, lm[i].visibility ?? 1);
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, i < 11 ? 3 : 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }
    } else {
      ctx.drawImage(img, 0, 0, w, h);

      if (results.poseLandmarks) {
        // Body only — face and finger points are clutter for a squat game.
        // Drawn by hand rather than via drawing_utils: the helpers skip
        // low-visibility landmarks, which severed bones whenever a joint left
        // the frame. MediaPipe still estimates off-frame joints, so we join
        // every bone and let the canvas clip naturally.
        const lm = results.poseLandmarks;
        const BODY = [11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28];
        const bodyConnections = (POSE_CONNECTIONS as [number, number][]).filter(
          ([a, b]) => a >= 11 && b >= 11 && a <= 28 && b <= 28
        );
        // One solid coloured skeleton per player (Joshua 2026-09-09: shared
        // camera "hard to tell who is who"): P1 green, P2 cyan, bold bones
        // over a dark outline so each figure reads as one colour on any
        // background.
        const two = !!results.p2Landmarks;
        const drawSkeleton = (marks: typeof lm, color: string) => {
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          for (const pass of [0, 1]) {
            ctx.strokeStyle = pass === 0 ? "rgba(0,0,0,0.7)" : color;
            ctx.lineWidth = pass === 0 ? (two ? 11 : 7) : (two ? 7 : 4);
            for (const [a, b] of bodyConnections) {
              const la = marks[a], lb = marks[b];
              if (!la || !lb) continue;
              ctx.beginPath();
              ctx.moveTo(la.x * w, la.y * h);
              ctx.lineTo(lb.x * w, lb.y * h);
              ctx.stroke();
            }
          }
          for (const i of BODY) {
            const l = marks[i];
            if (!l) continue;
            ctx.beginPath();
            ctx.arc(l.x * w, l.y * h, two ? 8 : 6, 0, Math.PI * 2);
            ctx.fillStyle = "rgba(0,0,0,0.7)";
            ctx.fill();
            ctx.beginPath();
            ctx.arc(l.x * w, l.y * h, two ? 6 : 5, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
          }
        };
        const win = winnerRef.current;
        drawSkeleton(lm, two ? (win === 1 ? "#fbbf24" : "#4ade80") : "#FFFFFF");
        if (!two) {
          // solo: keep the green joints on white bones the player knows
          ctx.fillStyle = "#4ade80";
          for (const i of BODY) {
            const l = lm[i];
            if (!l) continue;
            ctx.beginPath();
            ctx.arc(l.x * w, l.y * h, 5, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        // P1/P2 pills over each head - shared-camera mode was a guessing
        // game about who is who (Joshua 2026-09-01; bigger + filled
        // 2026-09-09). Canvas is mirrored, so unmirror with a scale flip.
        const tagPlayer = (marks: typeof lm, label: string, color: string) => {
          const head = marks[0] || marks[11];
          if (!head) return;
          const hx = head.x * w, hy = Math.max(30, head.y * h - 34);
          ctx.save();
          ctx.translate(hx, hy);
          ctx.scale(-1, 1);
          ctx.font = "900 26px system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          const pw = 72, ph = 40, r = 12;
          ctx.beginPath();
          ctx.roundRect(-pw / 2, -ph / 2, pw, ph, r);
          ctx.fillStyle = color;
          ctx.fill();
          ctx.lineWidth = 3;
          ctx.strokeStyle = "rgba(0,0,0,0.85)";
          ctx.stroke();
          ctx.fillStyle = "#000";
          ctx.fillText(label, 0, 1);
          ctx.restore();
        };
        // Winner celebration: gold skeleton, a crowned WINNER! pill and a
        // ring of sparkles around the head, for the seconds after the finish
        const GOLD = "#fbbf24";
        const celebrate = (marks: typeof lm) => {
          const head = marks[0] || marks[11];
          if (!head) return;
          const hx = head.x * w, hy = head.y * h;
          const tms = performance.now();
          ctx.save();
          for (let k = 0; k < 10; k++) {
            const ang = (k / 10) * Math.PI * 2 + tms / 700;
            const rad = 44 + 8 * Math.sin(tms / 180 + k);
            const sx = hx + Math.cos(ang) * rad, sy = hy - 6 + Math.sin(ang) * rad * 0.8;
            ctx.fillStyle = k % 2 ? GOLD : "#fff7cc";
            ctx.beginPath();
            for (let p = 0; p < 4; p++) {
              const a = (p / 4) * Math.PI * 2 + tms / 400;
              ctx.lineTo(sx + Math.cos(a) * 6, sy + Math.sin(a) * 6);
              ctx.lineTo(sx + Math.cos(a + Math.PI / 4) * 2, sy + Math.sin(a + Math.PI / 4) * 2);
            }
            ctx.closePath();
            ctx.fill();
          }
          ctx.translate(hx, Math.max(34, hy - 40));
          ctx.scale(-1, 1);
          const pulse = 1 + 0.06 * Math.sin(tms / 160);
          ctx.scale(pulse, pulse);
          ctx.font = "900 22px system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          const pw = 124, ph = 40;
          ctx.beginPath();
          ctx.roundRect(-pw / 2, -ph / 2, pw, ph, 12);
          ctx.fillStyle = GOLD;
          ctx.fill();
          ctx.lineWidth = 3;
          ctx.strokeStyle = "rgba(0,0,0,0.85)";
          ctx.stroke();
          ctx.fillStyle = "#000";
          ctx.fillText("👑 WINNER!", 0, 1);
          ctx.restore();
        };
        if (two) {
          if (win === 1) celebrate(lm);
          else tagPlayer(lm, "P1", "#4ade80");
        }

        // Player 2 in cyan (Joshua likes the cyan) - same bold treatment
        const lm2 = results.p2Landmarks;
        if (lm2) {
          drawSkeleton(lm2, win === 2 ? GOLD : "#22d3ee");
          if (win === 2) celebrate(lm2);
          else tagPlayer(lm2, "P2", "#22d3ee");
        }
      }
    }

    ctx.restore();
  }, [results, showPoints, isMobile]);

  const startGate =
    !started &&
    createPortal(
      <div
        onClick={startGame}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 40,
          background: "rgba(0,0,0,0.92)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        }}
      >
        <div style={{ fontSize: 34, fontWeight: 700, marginBottom: 12, opacity: gateReady ? 1 : 0.45 }}>
          {gateReady ? (isMobile ? "Tap to Start" : "Click to Start") : "Loading…"}
        </div>
        <div style={{ fontSize: 14, opacity: 0.7, textAlign: "center", padding: "0 32px" }}>
          {gateReady
            ? isMobile
              ? "Starting enables the camera, motion sensors and sound"
              : "Starting enables the camera and sound"
            : "Getting the game ready — this takes a moment"}
        </div>
      </div>,
      document.body
    );

  // Live readiness: the game only needs shoulders, so that's all we require
  const shouldersVisible = (() => {
    const lm = results?.poseLandmarks;
    if (!lm || lm.length < 13) return false;
    return (lm[11].visibility ?? 0) > 0.5 && (lm[12].visibility ?? 0) > 0.5;
  })();

  const ready = phoneSteady && shouldersVisible;

  // Tapping START means the player now reaches back up from the phone — that
  // lean reads as a squat. Unity freezes motion for a 3s countdown and wipes
  // whatever the tap registered.
  const dismissSetup = () => {
    setShowSetup(false);
    try {
      (window as any).unityInstance?.SendMessage("Manager", "BeginCountdown");
    } catch {}
  };

  const checkRow = (ok: boolean, label: string, hint: string) => (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
      <span style={{ fontSize: 18, color: ok ? "#4ade80" : "rgba(255,255,255,0.35)" }}>
        {ok ? "✓" : "○"}
      </span>
      <div>
        <div style={{ fontSize: 17, fontWeight: 600, opacity: ok ? 1 : 0.85 }}>{label}</div>
        {!ok && <div style={{ fontSize: 13, opacity: 0.6 }}>{hint}</div>}
      </div>
    </div>
  );

  const LAST_STEP = 4;

  const stepDots = (
    <div style={{ display: "flex", gap: 8, marginTop: 22 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            background: i === setupStep ? "#4ade80" : "rgba(255,255,255,0.25)",
          }}
        />
      ))}
    </div>
  );

  const figureStyle: React.CSSProperties = { width: "min(64vw, 280px)", marginBottom: 12 };
  const stroke = { stroke: "#fff", strokeWidth: 3.5, strokeLinecap: "round" as const, fill: "none" };

  const tutorialCards: { title: string; caption: string; art: React.ReactNode }[] = [
    {
      title: isMobile ? "Set your phone down" : "Stand back from the screen",
      caption: isMobile
        ? "Prop it on any surface and step back"
        : "Step back until the camera sees your whole body",
      art: (
        <svg viewBox="0 0 220 130" style={figureStyle}>
          <line x1="8" y1="118" x2="212" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
          <line x1="10" y1="76" x2="60" y2="76" stroke="rgba(255,255,255,0.35)" strokeWidth="3" />
          <line x1="15" y1="78" x2="15" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" />
          <line x1="55" y1="78" x2="55" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" />
          <rect x="16" y="56" width="14" height="19" rx="2" fill="rgba(255,255,255,0.22)" />
          <rect x="30" y="36" width="14" height="40" rx="3" fill="#e5e5e5" transform="rotate(12 37 56)" />
          <circle cx="41" cy="44" r="1.8" fill="#333" />
          <line x1="46" y1="44" x2="146" y2="32" stroke="rgba(110,220,255,0.45)" strokeWidth="1.5" strokeDasharray="4 5" />
          <line x1="46" y1="54" x2="146" y2="112" stroke="rgba(110,220,255,0.45)" strokeWidth="1.5" strokeDasharray="4 5" />
          <g {...stroke} style={{ animation: "poseStand 1.5s linear infinite" }}>
            <circle cx="160" cy="36" r="8" fill="#fff" stroke="none" />
            <line x1="160" y1="46" x2="160" y2="80" />
            <line x1="160" y1="54" x2="147" y2="72" />
            <line x1="160" y1="54" x2="173" y2="72" />
            <line x1="160" y1="80" x2="153" y2="118" />
            <line x1="160" y1="80" x2="167" y2="118" />
          </g>
          <g {...stroke} style={{ animation: "poseSquat 1.5s linear infinite" }}>
            <circle cx="160" cy="56" r="8" fill="#fff" stroke="none" />
            <line x1="160" y1="66" x2="160" y2="90" />
            <line x1="160" y1="72" x2="143" y2="80" />
            <line x1="160" y1="72" x2="145" y2="86" />
            <polyline points="160,90 146,101 150,118" />
            <polyline points="160,90 174,101 170,118" />
          </g>
        </svg>
      ),
    },
    {
      title: "Rapid short squats = RUN",
      caption: "Quick shallow bounces build your speed",
      art: (
        <svg viewBox="0 0 220 130" style={figureStyle}>
          <line x1="8" y1="118" x2="212" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
          {/* speed lines streaking past */}
          <g stroke="#4ade80" strokeWidth="3" strokeLinecap="round">
            <line x1="30" y1="52" x2="62" y2="52" style={{ animation: "speedLine 0.7s linear infinite" }} />
            <line x1="22" y1="72" x2="60" y2="72" style={{ animation: "speedLine 0.7s linear infinite 0.2s" }} />
            <line x1="34" y1="92" x2="64" y2="92" style={{ animation: "speedLine 0.7s linear infinite 0.45s" }} />
          </g>
          <g {...stroke} style={{ animation: "bobStand 0.55s linear infinite" }}>
            <circle cx="130" cy="36" r="8" fill="#fff" stroke="none" />
            <line x1="130" y1="46" x2="130" y2="80" />
            <line x1="130" y1="54" x2="117" y2="72" />
            <line x1="130" y1="54" x2="143" y2="72" />
            <line x1="130" y1="80" x2="123" y2="118" />
            <line x1="130" y1="80" x2="137" y2="118" />
          </g>
          <g {...stroke} style={{ animation: "bobSquat 0.55s linear infinite" }}>
            <circle cx="130" cy="46" r="8" fill="#fff" stroke="none" />
            <line x1="130" y1="56" x2="130" y2="86" />
            <line x1="130" y1="62" x2="116" y2="78" />
            <line x1="130" y1="62" x2="144" y2="78" />
            <polyline points="130,86 121,100 123,118" />
            <polyline points="130,86 139,100 137,118" />
          </g>
        </svg>
      ),
    },
    {
      title: "One deep squat = BOOST",
      caption: "Drop low for a burst of speed",
      art: (
        <svg viewBox="0 0 220 130" style={figureStyle}>
          <line x1="8" y1="118" x2="212" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
          {/* burst rays flash while the figure is down */}
          <g stroke="#fb923c" strokeWidth="3" strokeLinecap="round"
             style={{ animation: "burstFlash 1.8s linear infinite" }}>
            <line x1="70" y1="60" x2="92" y2="70" />
            <line x1="60" y1="92" x2="86" y2="92" />
            <line x1="70" y1="118" x2="92" y2="108" />
            <line x1="190" y1="60" x2="168" y2="70" />
            <line x1="200" y1="92" x2="174" y2="92" />
            <line x1="190" y1="118" x2="168" y2="108" />
          </g>
          <g {...stroke} style={{ animation: "poseStand 1.8s linear infinite" }}>
            <circle cx="130" cy="36" r="8" fill="#fff" stroke="none" />
            <line x1="130" y1="46" x2="130" y2="80" />
            <line x1="130" y1="54" x2="117" y2="72" />
            <line x1="130" y1="54" x2="143" y2="72" />
            <line x1="130" y1="80" x2="123" y2="118" />
            <line x1="130" y1="80" x2="137" y2="118" />
          </g>
          <g {...stroke} style={{ animation: "poseSquat 1.8s linear infinite" }}>
            <circle cx="130" cy="66" r="8" fill="#fff" stroke="none" />
            <line x1="130" y1="76" x2="130" y2="96" />
            <line x1="130" y1="80" x2="113" y2="88" />
            <line x1="130" y1="80" x2="147" y2="88" />
            <polyline points="130,96 114,105 118,118" />
            <polyline points="130,96 146,105 142,118" />
          </g>
        </svg>
      ),
    },
    {
      title: "Beat the orange racer!",
      caption: "Race to the white line — first one there wins",
      art: (
        <svg viewBox="0 0 220 130" style={figureStyle}>
          <line x1="8" y1="118" x2="212" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
          {/* finish line */}
          <line x1="196" y1="40" x2="196" y2="118" stroke="#fff" strokeWidth="4" strokeDasharray="7 6" />
          {/* the CPU rival, out in front */}
          <g stroke="#fb923c" strokeWidth="3.5" strokeLinecap="round" fill="none"
             style={{ animation: "bobStand 0.7s linear infinite" }}>
            <circle cx="140" cy="42" r="7" fill="#fb923c" stroke="none" />
            <line x1="140" y1="51" x2="140" y2="82" />
            <line x1="140" y1="58" x2="128" y2="74" />
            <line x1="140" y1="58" x2="152" y2="74" />
            <line x1="140" y1="82" x2="133" y2="118" />
            <line x1="140" y1="82" x2="147" y2="118" />
          </g>
          {/* you, chasing with speed lines */}
          <g stroke="#4ade80" strokeWidth="3" strokeLinecap="round">
            <line x1="18" y1="58" x2="46" y2="58" style={{ animation: "speedLine 0.7s linear infinite" }} />
            <line x1="14" y1="86" x2="44" y2="86" style={{ animation: "speedLine 0.7s linear infinite 0.3s" }} />
          </g>
          <g {...stroke} style={{ animation: "bobSquat 0.7s linear infinite" }}>
            <circle cx="86" cy="46" r="8" fill="#fff" stroke="none" />
            <line x1="86" y1="56" x2="86" y2="86" />
            <line x1="86" y1="62" x2="72" y2="78" />
            <line x1="86" y1="62" x2="100" y2="78" />
            <polyline points="86,86 77,100 79,118" />
            <polyline points="86,86 95,100 93,118" />
          </g>
        </svg>
      ),
    },
    {
      title: "Dodge the red spheres!",
      caption: "Walk side to side to dodge — get hit and you freeze",
      art: (
        <svg viewBox="0 0 220 130" style={figureStyle}>
          <line x1="8" y1="118" x2="212" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
          <circle cx="0" cy="78" r="11" fill="#ef4444"
                  style={{ animation: "sphereFly 2.2s linear infinite" }} />
          <g {...stroke} style={{ animation: "dodgeStep 2.2s linear infinite" }}>
            <circle cx="150" cy="36" r="8" fill="#fff" stroke="none" />
            <line x1="150" y1="46" x2="150" y2="80" />
            <line x1="150" y1="54" x2="137" y2="72" />
            <line x1="150" y1="54" x2="163" y2="72" />
            <line x1="150" y1="80" x2="143" y2="118" />
            <line x1="150" y1="80" x2="157" y2="118" />
          </g>
        </svg>
      ),
    },
  ];

  const card = tutorialCards[setupStep];

  const setupScreen =
    started &&
    showSetup &&
    createPortal(
      <div
        onClick={() => setupStep < LAST_STEP && setSetupStep(setupStep + 1)}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 40,
          background: "rgba(0,0,0,0.92)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          padding: "0 28px",
          textAlign: "left",
          overflowY: "auto",
        }}
      >
        <style>{`
          @keyframes poseStand { 0%, 42% { opacity: 1; } 50%, 92% { opacity: 0; } 100% { opacity: 1; } }
          @keyframes poseSquat { 0%, 42% { opacity: 0; } 50%, 92% { opacity: 1; } 100% { opacity: 0; } }
          @keyframes bobStand { 0%, 45% { opacity: 1; } 55%, 95% { opacity: 0; } 100% { opacity: 1; } }
          @keyframes bobSquat { 0%, 45% { opacity: 0; } 55%, 95% { opacity: 1; } 100% { opacity: 0; } }
          @keyframes speedLine { 0% { transform: translateX(14px); opacity: 0; }
                                 30% { opacity: 1; } 100% { transform: translateX(-26px); opacity: 0; } }
          @keyframes burstFlash { 0%, 48% { opacity: 0; } 55%, 85% { opacity: 1; } 95%, 100% { opacity: 0; } }
          @keyframes sphereFly { 0% { transform: translateX(20px) scale(0.55); opacity: 0; }
                                 12% { opacity: 1; }
                                 70% { transform: translateX(150px) scale(1); opacity: 1; }
                                 100% { transform: translateX(230px) scale(1.25); opacity: 0; } }
          @keyframes dodgeStep { 0%, 40% { transform: translateX(0); }
                                 55%, 80% { transform: translateX(26px); }
                                 95%, 100% { transform: translateX(0); } }
        `}</style>

        <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 6, textAlign: "center" }}>{card.title}</div>
        <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 10, textAlign: "center" }}>{card.caption}</div>
        {card.art}

        {setupStep < LAST_STEP ? (
          <div style={{ fontSize: 15, opacity: 0.6, marginTop: 8 }}>Tap to continue →</div>
        ) : (
          <>
            <div style={{ marginBottom: 14 }}>
              {checkRow(phoneSteady, "Phone is steady", "Lean it against something — don't hold it")}
              {checkRow(shouldersVisible, "We can see you", "Step back until your shoulders are in view")}
            </div>
            <button
              onClick={(ev) => { ev.stopPropagation(); if (ready) dismissSetup(); }}
              style={{
                fontSize: 19,
                fontWeight: 700,
                padding: "12px 44px",
                borderRadius: 28,
                border: "none",
                color: ready ? "#000" : "rgba(255,255,255,0.5)",
                background: ready ? "#4ade80" : "rgba(255,255,255,0.15)",
                transition: "background 0.25s",
              }}
            >
              START
            </button>
            {!ready && (
              <div
                onClick={(ev) => { ev.stopPropagation(); dismissSetup(); }}
                style={{ fontSize: 13, opacity: 0.4, marginTop: 12, textDecoration: "underline" }}
              >
                start anyway
              </div>
            )}
          </>
        )}
        {stepDots}
      </div>,
      document.body
    );

  // Two cards per mode: (title, caption, stick-figure art). Same visual
  // language as the setup tutorial.
  const modeCardScreen =
    modeCards &&
    createPortal(
      <div
        onClick={advanceModeCard}
        style={{
          position: "fixed", inset: 0, zIndex: 45,
          background: "rgba(0,0,0,0.92)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          color: "#fff", padding: "0 28px", textAlign: "center",
        }}
      >
        <style>{`
          @keyframes swingArc { 0%, 40% { opacity: 0; transform: rotate(-50deg); }
                                55%, 75% { opacity: 1; transform: rotate(40deg); }
                                100% { opacity: 0; transform: rotate(40deg); } }
          @keyframes fallBits { 0%, 50% { opacity: 1; transform: translateY(0); }
                                100% { opacity: 0; transform: translateY(26px); } }
          @keyframes tidePush { 0%, 20% { width: 30%; } 80%, 100% { width: 74%; } }
        `}</style>
        {(() => {
          const zombie = modeCards.mode === 1;
          const s = { stroke: "#fff", strokeWidth: 3.5, strokeLinecap: "round" as const, fill: "none" };
          const foe = { stroke: zombie ? "#8fbc6f" : "#e06050", strokeWidth: 3.5, strokeLinecap: "round" as const, fill: "none" };
          const cards = zombie
            ? [
                {
                  t: "SWING = SLASH", c: "Big arm swings cut them down",
                  art: (
                    <svg viewBox="0 0 220 130" style={{ width: "min(64vw, 280px)", marginBottom: 12 }}>
                      <line x1="8" y1="118" x2="212" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
                      <g {...s}>
                        <circle cx="70" cy="40" r="8" fill="#fff" stroke="none" />
                        <line x1="70" y1="50" x2="70" y2="84" />
                        <line x1="70" y1="84" x2="63" y2="118" /><line x1="70" y1="84" x2="77" y2="118" />
                        <line x1="70" y1="58" x2="56" y2="74" />
                      </g>
                      <g style={{ transformOrigin: "70px 58px", animation: "swingArc 1.6s linear infinite" }}>
                        <line x1="70" y1="58" x2="106" y2="40" stroke="#7dd8ff" strokeWidth="4" strokeLinecap="round" />
                      </g>
                      <g {...foe} style={{ animation: "fallBits 1.6s linear infinite" }}>
                        <circle cx="150" cy="46" r="8" fill={zombie ? "#8fbc6f" : "#e06050"} stroke="none" />
                        <line x1="150" y1="56" x2="146" y2="80" /><line x1="152" y1="62" x2="166" y2="70" />
                        <line x1="146" y1="80" x2="140" y2="104" /><line x1="150" y1="80" x2="160" y2="102" />
                      </g>
                    </svg>
                  ),
                },
                {
                  t: "DON'T GET BITTEN", c: "5 hearts · step aside or kill · slay the boss at the end",
                  art: (
                    <svg viewBox="0 0 220 130" style={{ width: "min(64vw, 280px)", marginBottom: 12 }}>
                      <line x1="8" y1="118" x2="212" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
                      <text x="110" y="30" fill="#f87171" fontSize="20" textAnchor="middle">♥ ♥ ♥ ♥ ♥</text>
                      <g {...s}>
                        <circle cx="80" cy="52" r="8" fill="#fff" stroke="none" />
                        <line x1="80" y1="62" x2="80" y2="90" />
                        <line x1="80" y1="90" x2="73" y2="118" /><line x1="80" y1="90" x2="87" y2="118" />
                      </g>
                      <g {...foe}>
                        <circle cx="132" cy="46" r="10" fill={zombie ? "#8fbc6f" : "#e06050"} stroke="none" />
                        <line x1="132" y1="58" x2="132" y2="92" />
                        <line x1="132" y1="66" x2="112" y2="60" /><line x1="132" y1="66" x2="112" y2="74" />
                        <line x1="132" y1="92" x2="124" y2="118" /><line x1="132" y1="92" x2="140" y2="118" />
                      </g>
                    </svg>
                  ),
                },
              ]
            : [
                {
                  t: "WAR!", c: "Squat to advance into the battle · arms overhead + squat to retreat",
                  art: (
                    <svg viewBox="0 0 220 130" style={{ width: "min(64vw, 280px)", marginBottom: 12 }}>
                      <line x1="8" y1="118" x2="212" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
                      {[0, 1, 2].map(i => (
                        <g key={i} stroke="#7aa8ff" strokeWidth="3" strokeLinecap="round" fill="none">
                          <circle cx={40 + i * 18} cy={50 + i * 8} r="6" fill="#7aa8ff" stroke="none" />
                          <line x1={40 + i * 18} y1={58 + i * 8} x2={40 + i * 18} y2={86 + i * 8} />
                          <line x1={40 + i * 18} y1={86 + i * 8} x2={35 + i * 18} y2={112 + i * 4} />
                          <line x1={40 + i * 18} y1={86 + i * 8} x2={45 + i * 18} y2={112 + i * 4} />
                        </g>
                      ))}
                      {[0, 1, 2].map(i => (
                        <g key={i} stroke="#e06050" strokeWidth="3" strokeLinecap="round" fill="none">
                          <circle cx={180 - i * 18} cy={50 + i * 8} r="6" fill="#e06050" stroke="none" />
                          <line x1={180 - i * 18} y1={58 + i * 8} x2={180 - i * 18} y2={86 + i * 8} />
                          <line x1={180 - i * 18} y1={86 + i * 8} x2={175 - i * 18} y2={112 + i * 4} />
                          <line x1={180 - i * 18} y1={86 + i * 8} x2={185 - i * 18} y2={112 + i * 4} />
                        </g>
                      ))}
                      <text x="110" y="45" fill="#fff" fontSize="26" textAnchor="middle">⚔</text>
                    </svg>
                  ),
                },
                {
                  t: "TURN THE TIDE", c: "Slash orcs · stay with your army — the Warlord's rocks hit whoever's in range · slay him to win",
                  art: (
                    <svg viewBox="0 0 220 130" style={{ width: "min(64vw, 280px)", marginBottom: 12 }}>
                      <rect x="20" y="20" width="180" height="12" rx="3" fill="#e06050" />
                      <rect x="20" y="20" height="12" rx="3" fill="#7aa8ff"
                            style={{ animation: "tidePush 2.2s ease-in-out infinite" }} />
                      <line x1="8" y1="118" x2="212" y2="118" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
                      <g {...s}>
                        <circle cx="90" cy="60" r="8" fill="#fff" stroke="none" />
                        <line x1="90" y1="70" x2="90" y2="94" />
                        <polyline points="90,94 78,104 82,118" /><polyline points="90,94 102,104 98,118" />
                        <line x1="90" y1="76" x2="112" y2="64" />
                      </g>
                      <line x1="112" y1="64" x2="132" y2="52" stroke="#7dd8ff" strokeWidth="4" strokeLinecap="round" />
                      <g {...foe}>
                        <circle cx="168" cy="56" r="11" fill="#e06050" stroke="none" />
                        <line x1="168" y1="69" x2="168" y2="96" />
                        <line x1="168" y1="96" x2="160" y2="118" /><line x1="168" y1="96" x2="176" y2="118" />
                      </g>
                    </svg>
                  ),
                },
              ];
          const card = cards[modeCards.step];
          return (
            <>
              <div style={{ fontSize: 26, fontWeight: 700, marginBottom: 6 }}>{card.t}</div>
              <div style={{ fontSize: 14, opacity: 0.75, marginBottom: 10 }}>{card.c}</div>
              {card.art}
              <div style={{ fontSize: 15, opacity: 0.6, marginTop: 8 }}>
                {modeCards.step < 1 ? "Tap to continue →" : "Tap to fight!"}
              </div>
            </>
          );
        })()}
      </div>,
      document.body
    );

  // "Help us improve" consent card — same dark-card look as the mode cards.
  const shareCardScreen =
    showShareCard &&
    createPortal(
      <div
        onClick={() => setShowShareCard(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 45,
          background: "rgba(0,0,0,0.92)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          color: "#fff", padding: "0 28px", textAlign: "center",
        }}
      >
        <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
          <div style={{ fontSize: 26, fontWeight: 700, marginBottom: 10 }}>HELP US IMPROVE</div>
          <div style={{ fontSize: 15, opacity: 0.85, lineHeight: 1.5, marginBottom: 6 }}>
            Share anonymous motion data to make the game track your moves better.
          </div>
          <div style={{ fontSize: 13, opacity: 0.6, lineHeight: 1.5, marginBottom: 18 }}>
            No video ever leaves your device — just stick-figure joint positions,
            with no name or account attached. Only records while you play a
            level, never on the menu. Turn it off any time.
          </div>
          <button
            onClick={() => {
              const next = !poseShare;
              setPoseOptIn(next);
              setPoseShareState(next);
            }}
            style={{
              fontSize: 17, fontWeight: 700, padding: "12px 34px",
              borderRadius: 12, border: "none", cursor: "pointer",
              background: poseShare ? "#16a34a" : "rgba(255,255,255,0.15)",
              color: "#fff",
            }}
          >
            {poseShare ? "SHARING ON  ✓" : "TURN ON SHARING"}
          </button>
          <div
            onClick={() => setShowShareCard(false)}
            style={{ fontSize: 14, opacity: 0.6, marginTop: 18, cursor: "pointer" }}
          >
            close
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div
      className="webcamContainer"
      style={{
        // Fixed overlay: inline display beats the mobile media query that
        // hides this container, and the game canvas sits underneath
        position: "fixed",
        // Top-right corner; the progress bar + gap readout live at the BOTTOM
        // of the screen now, so the feed only shares the top with the narrow
        // centered timer and never blocks HOLD STILL / FROZEN (mid-screen)
        // (+ the status bar inset when launched from the home screen)
        top: "calc(8px + env(safe-area-inset-top, 0px))",
        right: 8,
        // capture mode: the feed must float OVER the capture card so the
        // player can actually see themselves while framing up
        zIndex: getIsCapture() ? 60 : 25,
        display: "block",
        // Desktop shows the full landscape frame, so it gets a bit wider
        width: feedVisible ? (isMobile ? "min(34vw, 210px)" : "min(34vw, 280px)") : "auto",
        margin: 0,
        padding: 0,
      }}
    >
      {startGate}
      {setupScreen}
      {modeCardScreen}
      {shareCardScreen}
      <Webcam
        key={camKey}
        ref={webcamRef}
        onUserMedia={(stream: MediaStream) => {
          streamRef.current = stream;
          const t = stream.getVideoTracks()[0];
          if (!t) return;
          t.onended = () => reacquire();
          // iOS mutes the track while the page is covered and unmutes on
          // return - the video element needs a play() then, nothing more
          t.onunmute = () => { const v = webcamRef.current?.video as HTMLVideoElement | undefined; if (v) v.play().catch(() => {}); };
        }}
        style={{
          position: "relative",
          marginLeft: "auto",
          marginRight: "auto",
          textAlign: "center",
          zIndex: 9,
          display: "none",
        }}
        audio={false}
        videoConstraints={{
          width: 640,
          height: 480,
          facingMode: "user",
        }}
      />
      {/* Phones: the camera is 4:3 landscape but the subject is a standing
          person — crop to a centred portrait window so they fill the frame.
          Desktop: show exactly what the webcam sees, in its own aspect.
          Wrapper: the depth meter measures THIS frame, not the buttons
          below it (Joshua 2026-09-04) */}
      <div style={{ position: "relative" }}>
      <div
        onClick={() => setFeedVisible(false)}
        style={{
          display: feedVisible ? "block" : "none",
          width: "100%",
          aspectRatio: isMobile ? "3 / 4" : undefined,
          overflow: "hidden",
          borderRadius: 12,
          border: flashColor ? `3px solid ${flashColor}` : "1px solid rgba(255,255,255,0.4)",
          boxShadow: flashColor ? `0 0 14px ${flashColor}` : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
          position: "relative",
          background: "#000",
        }}
      >
        <canvas
          ref={canvasRef}
          style={isMobile ? {
            position: "absolute",
            height: "100%",
            width: "auto",
            left: "50%",
            // Mirror like every selfie view — moving left should look like left
            transform: "translateX(-50%)",
          } : {
            // Desktop: the canvas sizes the box at the camera's true aspect
            display: "block",
            width: "100%",
            height: "auto",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: 4,
            left: 0,
            right: 0,
            textAlign: "center",
            fontSize: 10,
            color: "rgba(255,255,255,0.75)",
            textShadow: "0 1px 2px rgba(0,0,0,0.8)",
            pointerEvents: "none",
          }}
        >
          tap to close
        </div>
        {benchInfo && (
          <div
            style={{
              position: "absolute",
              top: 4,
              left: 4,
              fontSize: 11,
              fontWeight: 700,
              color: "#facc15",
              textShadow: "0 1px 2px rgba(0,0,0,0.9)",
              pointerEvents: "none",
            }}
          >
            {benchInfo}
          </div>
        )}
        {engineError && (
          <div
            style={{
              position: "absolute",
              top: 4,
              left: 4,
              right: 4,
              fontSize: 9,
              color: "#f87171",
              textShadow: "0 1px 2px rgba(0,0,0,0.9)",
              pointerEvents: "none",
              wordBreak: "break-all",
            }}
          >
            tracker error: {engineError}
          </div>
        )}
      </div>
      {feedVisible && (
        <>
          <style>{`@keyframes peakFade { 0%, 55% { opacity: 1; } 100% { opacity: 0; } }`}</style>
          {/* Depth meter OUTSIDE the feed's left edge — the gold high-water
              mark gets open space instead of fighting the video for contrast */}
          <div
            style={{
              position: "absolute",
              left: -18,
              top: 6,
              height: "calc(100% - 12px)",
              width: 8,
              borderRadius: 4,
              background: "rgba(0,0,0,0.6)",
              border: "1px solid rgba(255,255,255,0.35)",
              boxSizing: "border-box",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: `${depthLevel * 100}%`,
                borderRadius: 3,
                background: repFlash === "burst" ? "#fb923c" : "#4ade80",
                boxShadow: depthLevel > 0 ? `0 0 8px ${repFlash === "burst" ? "#fb923c" : "#4ade80"}` : "none",
                transition: depthLevel > 0 ? "height 0.08s ease-out" : "height 0.5s ease-in",
              }}
            />
            {peakMark > 0 && (
              <div
                style={{
                  position: "absolute",
                  bottom: `${peakMark * 100}%`,
                  left: -7,
                  width: 20,
                  height: 3,
                  borderRadius: 2,
                  background: "#facc15",
                  boxShadow: "0 0 8px rgba(250,204,21,0.9)",
                  animation: "peakFade 2s linear forwards",
                }}
              />
            )}
          </div>
        </>
      )}
      </div>
      {/* camera pills: hidden mid-run (Joshua 2026-11-04 declutter) */}
      {feedVisible && !running && (
        <button
          onClick={() => setShowPoints(!showPoints)}
          style={{
            display: "block",
            // fixed width: the label swap ("keypoints" / "camera view") used to
            // resize this pill and shift "help improve" under the thumb -
            // an actual misclick in the crawl (2026-09-04); extra top margin
            // breathes from the camera panel
            minWidth: 92,
            textAlign: "center",
            margin: "8px 0 0 auto",
            fontSize: 10,
            padding: "3px 10px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.35)",
            background: "rgba(0,0,0,0.5)",
            color: showPoints ? "#facc15" : "rgba(255,255,255,0.8)",
          }}
        >
          {showPoints ? "camera view" : "keypoints"}
        </button>
      )}
      {feedVisible && !running && (
        <button
          onClick={() => setShowShareCard(true)}
          style={{
            display: "block",
            minWidth: 92,
            textAlign: "center",
            margin: "4px 0 0 auto",
            fontSize: 10,
            padding: "3px 10px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.35)",
            background: "rgba(0,0,0,0.5)",
            color: poseShare ? "#4ade80" : "rgba(255,255,255,0.8)",
          }}
        >
          {poseShare ? "sharing ✓" : "help improve"}
        </button>
      )}
      {/* Prominent fullscreen launcher (Joshua 2026-08-29: "more clear") —
          fixed top-left, opposite the camera feed, gone once fullscreen */}
      {started && !inFullscreen && !standalone && !running && !isIOS &&
        createPortal(
          <button
            onClick={enterFullscreen}
            style={{
              position: "fixed",
              top: "calc(10px + env(safe-area-inset-top, 0px))",
              left: 10,
              zIndex: 26,
              fontSize: 13,
              fontWeight: 700,
              padding: "8px 14px",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.45)",
              background: "rgba(11,16,32,0.8)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 15, lineHeight: 1 }}>⛶</span> FULLSCREEN
          </button>,
          document.body
        )}
      {!feedVisible && (
        <button
          onClick={() => setFeedVisible(true)}
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            border: flashColor ? `3px solid ${flashColor}` : "1px solid rgba(255,255,255,0.4)",
            boxShadow: flashColor ? `0 0 14px ${flashColor}` : "none",
            transition: "border-color 0.15s, box-shadow 0.15s",
            background: "rgba(0,0,0,0.55)",
            color: "#fff",
            fontSize: 20,
          }}
        >
          &#128247;
        </button>
      )}
    </div>
  );
};

export default WebcamPoseProvider;
